/**
 * WorkerOrderCard Component
 *
 * Displays detailed information about a worker's active order
 * Includes order details, client info, status, agreed price, and description
 * Shows "Mark as Complete" button for IN_PROGRESS orders
 */

'use client';

import { FC, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { OrderStatusBadge } from '@/src/shared/ui/badges/OrderStatusBadge';
import type { OrderResponse } from '@/src/shared/api/orders.api';
import { MapPin, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import { cn } from '@/src/shared/lib/utils';
import { OrderReviewStars } from '@/src/features/reviews';

interface WorkerOrderCardProps {
  order: OrderResponse;
  onComplete?: (orderId: string) => void;
}

export const WorkerOrderCard: FC<WorkerOrderCardProps> = ({ order, onComplete }) => {
  const t = useTranslations('myWorker.orders');
  const router = useRouter();
  const [isCompleting, setIsCompleting] = useState(false);

  const handleViewOrder = () => {
    router.push(`/orders/${order.id}`);
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onComplete) {
      setIsCompleting(true);
      onComplete(order.id);
    }
  };

  const clientActor = order.clientActor;
  const city = order.city;
  const province = order.province;
  const startDate = order.startedAt ? new Date(order.startedAt) : null;
  const isInProgress = order.status === 'IN_PROGRESS';

  return (
    <div className="p-5 bg-white rounded-xl border border-border-primary shadow-sm hover:shadow-md transition-shadow">
      {/* Header: Order title + Status */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1">
          <Typography variant="h4" weight="semibold" color="primary">
            {order.title}
          </Typography>
          {/* Review Stars - show for COMPLETED orders */}
          {order.status === 'COMPLETED' && clientActor && (
            <OrderReviewStars
              orderId={order.id}
              userRole="worker"
              orderTitle={order.title}
              clientName={clientActor.displayName || clientActor.name}
              clientAvatarUrl={clientActor.avatarUrl}
              className="mt-1"
            />
          )}
        </div>
        <OrderStatusBadge status={order.status} />
      </div>

      {/* Client info */}
      {clientActor && (
        <div className="flex items-center gap-3 mb-4">
          <Avatar
            src={clientActor.avatarUrl}
            alt={clientActor.name}
            size="sm"
            fallback={clientActor.name.charAt(0).toUpperCase()}
          />
          <Typography variant="body" color="secondary">
            {clientActor.displayName || clientActor.name}
          </Typography>
        </div>
      )}

      {/* Order details grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Location */}
        {(city || province) && (
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0" />
            <Typography variant="bodySm" color="secondary" className="truncate">
              {city}
              {province && `, ${province}`}
            </Typography>
          </div>
        )}

        {/* Start date */}
        {startDate && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-text-tertiary flex-shrink-0" />
            <Typography variant="bodySm" color="secondary">
              {startDate.toLocaleDateString()}
            </Typography>
          </div>
        )}

        {/* Agreed price */}
        {order.agreedPrice && (
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-text-tertiary flex-shrink-0" />
            <Typography variant="body" weight="medium" color="primary">
              ${order.agreedPrice}
            </Typography>
          </div>
        )}
      </div>

      {/* Order description (truncated) */}
      {order.description && (
        <div className="mb-4">
          <Typography variant="bodySm" color="tertiary" className="line-clamp-3">
            {order.description}
          </Typography>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col gap-2">
        {/* Mark as Complete button for IN_PROGRESS orders */}
        {isInProgress && onComplete && (
          <button
            onClick={handleComplete}
            disabled={isCompleting}
            className={cn(
              'w-full py-2.5 px-4 rounded-lg font-medium transition-colors',
              'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed',
              'flex items-center justify-center gap-2'
            )}
          >
            <CheckCircle className="w-4 h-4" />
            {isCompleting ? t('markingComplete') : t('markAsComplete')}
          </button>
        )}

        {/* View Order button */}
        <button
          onClick={handleViewOrder}
          className={cn(
            'w-full py-2.5 px-4 rounded-lg font-medium transition-colors',
            isInProgress ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-[#6AB344] text-white hover:bg-[#5A9B38]'
          )}
        >
          {t('viewOrder')}
        </button>
      </div>
    </div>
  );
};
